import React, { useState, useCallback, useEffect } from "react";
import { Stack } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";
import dayjs from "dayjs";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  DateRangeSelector,
  DateRangeValue,
} from "@/components/generic";
import {
  RecurringEventsTable,
  RecurringEventFormModal,
  VenueSelector,
  NeedAddVenue,
} from "@/components/features";
import { useRecurringEvents } from "@/hooks";
import { useVenuesContext } from "@/contexts";
import { RecurringEvent, Pagination, RecurringEventFilter } from "@/utils";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export const RecurringEventsPage: React.FC<PageProps> = (props: PageProps) => {
  // Get selected venue from context
  const {
    loading: venuesLoading,
    selectedVenue,
    setSelectedVenue,
    hasVenues,
  } = useVenuesContext();

  // Selected venue for events
  const [eventVenueId, setEventVenueId] = useState<string | null>(
    selectedVenue?.id || null
  );

  // Date range state
  const [dateRange, setDateRange] = useState<DateRangeValue>({
    startDate: dayjs().format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
    viewMode: "day",
    displayDate: "",
  });

  // Track if initial load has happened
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  // Recurring events hook
  const {
    recurringEvents,
    loading: loadingRecurring,
    loadingMore: loadingMoreRecurring,
    hasMore: hasMoreRecurring,
    isEmpty: isEmptyRecurring,
    sortBy: recurringSortBy,
    isCreating: isCreatingRecurring,
    isUpdating: isUpdatingRecurring,
    removingRecurringEventId,
    selectedRecurringEvent,
    list: listRecurringEvents,
    loadMore: loadMoreRecurring,
    setSortBy: setRecurringSortBy,
    create: createRecurringEvent,
    update: updateRecurringEvent,
    remove: removeRecurringEvent,
    setSelectedRecurringEvent,
    lastError: recurringLastError,
    clearError: clearRecurringError,
  } = useRecurringEvents({
    pageSize: 20,
    autoLoad: false,
  });

  // Local UI state
  const [recurringFormModalOpened, setRecurringFormModalOpened] =
    useState<boolean>(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<RecurringEvent | null>(null);

  // Load recurring events with specific parameters
  const loadRecurringEvents = useCallback(
    (venueId: string, currentDateRange: DateRangeValue) => {
      console.log(
        "Loading recurring events with date range:",
        currentDateRange
      );

      const venueFilter = { venueId: [venueId] };
      const pagination: Pagination = { page: 1, pageSize: DEFAULT_PAGE_SIZE };

      let filter: Partial<RecurringEventFilter>;

      if (currentDateRange.viewMode === "day") {
        filter = {
          ...venueFilter,
          startDate: currentDateRange.startDate,
          endDate: currentDateRange.startDate,
        };
      } else {
        filter = {
          ...venueFilter,
          startDate: currentDateRange.startDate,
          endDate: currentDateRange.endDate,
        };
      }

      const sortConfig: SortConfig[] = [
        { key: "recurringEvents.name", direction: "asc" },
      ];
      console.log("Loading recurring events with filter:", filter);
      listRecurringEvents(filter, pagination, sortConfig, true);
    },
    [listRecurringEvents]
  );

  // Initial load effect
  useEffect(() => {
    if (eventVenueId && !hasInitialLoad) {
      setHasInitialLoad(true);
      loadRecurringEvents(eventVenueId, dateRange);
    }
  }, [eventVenueId, hasInitialLoad, dateRange, loadRecurringEvents]);

  // Update event venue ID when selected venue changes
  useEffect(() => {
    if (selectedVenue?.id !== eventVenueId) {
      setEventVenueId(selectedVenue?.id || null);
      setHasInitialLoad(false);
    }
  }, [selectedVenue?.id, eventVenueId]);

  // Handle venue selection
  const handleVenueChange = useCallback(
    (venueId: string | null, venue: any) => {
      setEventVenueId(venueId);
      setSelectedVenue(venue);
      setHasInitialLoad(false);

      if (venueId) {
        loadRecurringEvents(venueId, dateRange);
      }
    },
    [setSelectedVenue, dateRange, loadRecurringEvents]
  );

  // Handle date range change
  const handleDateRangeChange = useCallback(
    (newDateRange: DateRangeValue) => {
      console.log("Date range changed:", newDateRange);
      setDateRange(newDateRange);
      clearRecurringError();

      if (eventVenueId) {
        loadRecurringEvents(eventVenueId, newDateRange);
      }
    },
    [clearRecurringError, eventVenueId, loadRecurringEvents]
  );

  // Handle add recurring event button click
  const handleAddRecurringEvent = useCallback(() => {
    setSelectedRecurringEvent(null);
    setRecurringFormModalOpened(true);
  }, [setSelectedRecurringEvent]);

  // Handle edit recurring event
  const handleEditRecurringEvent = useCallback(
    (event: RecurringEvent) => {
      setSelectedRecurringEvent(event);
      setRecurringFormModalOpened(true);
    },
    [setSelectedRecurringEvent]
  );

  // Handle view recurring event
  const handleViewRecurringEvent = useCallback((event: RecurringEvent) => {
    console.log("View recurring event:", event);
  }, []);

  // Handle remove button click
  const handleRemoveRecurringEventClick = useCallback(
    (event: RecurringEvent) => {
      setItemToDelete(event);
      setDeleteModalOpened(true);
    },
    []
  );

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (itemToDelete?.id) {
      const success = await removeRecurringEvent(itemToDelete.id);

      if (success) {
        setDeleteModalOpened(false);
        setItemToDelete(null);
      }
    }
  }, [itemToDelete, removeRecurringEvent]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setDeleteModalOpened(false);
    setItemToDelete(null);
  }, []);

  // Handle form modal close
  const handleRecurringFormModalClose = useCallback(() => {
    setRecurringFormModalOpened(false);
    setSelectedRecurringEvent(null);
    clearRecurringError();
  }, [setSelectedRecurringEvent, clearRecurringError]);

  // Handle refresh - explicit user action
  const handleRefresh = useCallback(() => {
    if (eventVenueId) {
      loadRecurringEvents(eventVenueId, dateRange);
    }
  }, [eventVenueId, dateRange, loadRecurringEvents]);

  // Handle recurring event creation
  const handleCreateRecurringEvent = useCallback(
    async (eventData: any) => {
      const result = await createRecurringEvent(eventData);
      if (result) {
        setRecurringFormModalOpened(false);
        setSelectedRecurringEvent(null);
      }
      return result;
    },
    [createRecurringEvent, setSelectedRecurringEvent]
  );

  // Handle recurring event update
  const handleUpdateRecurringEvent = useCallback(
    async (eventId: string, eventData: any) => {
      const result = await updateRecurringEvent(eventId, eventData);
      if (result) {
        setRecurringFormModalOpened(false);
        setSelectedRecurringEvent(null);
      }
      return result;
    },
    [updateRecurringEvent, setSelectedRecurringEvent]
  );

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Venue selector and controls toolbar */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "venue-selector",
              items: [
                <VenueSelector
                  key="venue-selector"
                  value={eventVenueId}
                  onChange={handleVenueChange}
                  placeholder="Select venue to manage recurring events"
                  style={{ minWidth: 250 }}
                  required
                  disabled={venuesLoading}
                />,
                <AppButton
                  key="add-event"
                  iconName="Plus"
                  onClick={handleAddRecurringEvent}
                  disabled={
                    !eventVenueId || loadingRecurring || isCreatingRecurring
                  }
                >
                  Add
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={!eventVenueId || loadingRecurring}
                >
                  Refresh
                </AppButton>,
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "date-controls",
              items: [
                <DateRangeSelector
                  key="date-range-selector"
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  disabled={!eventVenueId || loadingRecurring}
                />,
              ],
              align: "right",
            },
          ]}
        />

        {/* Recurring events content */}
        <RecurringEventsTable
          recurringEvents={recurringEvents}
          loading={loadingRecurring || venuesLoading}
          loadingMore={loadingMoreRecurring}
          hasMore={hasMoreRecurring}
          sortBy={recurringSortBy}
          onSortChange={setRecurringSortBy}
          onLoadMore={loadMoreRecurring}
          onEditRecurringEvent={handleEditRecurringEvent}
          onRemoveRecurringEvent={handleRemoveRecurringEventClick}
          onViewRecurringEvent={handleViewRecurringEvent}
          removingRecurringEventId={removingRecurringEventId}
          size="md"
        />

        <EmptyState
          display={
            !!selectedVenue?.id &&
            isEmptyRecurring &&
            !loadingRecurring &&
            !venuesLoading
          }
          title="No recurring events"
          description={`No recurring events found for ${dateRange.displayDate.toLowerCase()}`}
          actionIcon="Plus"
          actionLabel="Add Recurring Event"
          onAction={handleAddRecurringEvent}
        />

        {/* Empty state for no venue exist */}
        <NeedAddVenue
          display={!hasVenues && !loadingRecurring && !venuesLoading}
          descriptionExtra="Then add rooms in it and then you can start adding recurring events"
        />

        {/* Empty state for no venue selected */}
        <EmptyState
          display={!selectedVenue?.id && hasVenues}
          title="Select a venue"
          description="Choose a venue from the dropdown to view its recurring events"
        />
      </Stack>

      {/* Recurring event form modal */}
      <RecurringEventFormModal
        opened={recurringFormModalOpened}
        onClose={handleRecurringFormModalClose}
        recurringEvent={selectedRecurringEvent}
        venueId={eventVenueId || ""}
        onCreateRecurringEvent={handleCreateRecurringEvent}
        onUpdateRecurringEvent={handleUpdateRecurringEvent}
        isCreating={isCreatingRecurring}
        isUpdating={isUpdatingRecurring}
        lastError={recurringLastError}
        onClearError={clearRecurringError}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={deleteModalOpened}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Recurring Event"
        message={`Are you sure you want to delete "${itemToDelete?.name}"?`}
        details="This will also delete all generated single events from this recurring pattern. This action cannot be undone."
        confirmInProgress={removingRecurringEventId === itemToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
